import { BreakpointObserver } from '@angular/cdk/layout';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { MaintenanceService } from '@core/error-pages/maintenance-page/services/maintenance.service';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { StorageServiceKeysMap } from '@core/storage/types/storage.types';
import { ImpersonationBannerComponent } from '@features/authentication/components/impersonation-banner/impersonation-banner.component';
import {
  ErrorSeverityMap,
  MaintenanceWarningComponent,
  NAVIGATION_BREAKPOINTS,
  NavigationFacade,
  StorageService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { FooterComponent } from './footer/footer.component';
import { SidebarComponent } from './sidebar/sidebar.component';

@Component({
  selector: 'aad-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.less',
  imports: [
    NzLayoutModule,
    RouterOutlet,
    FooterComponent,
    SidebarComponent,
    MaintenanceWarningComponent,
    ImpersonationBannerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  private readonly maintenanceService = inject(MaintenanceService);

  private readonly translationService = inject(TranslationService);

  private readonly storageService = inject(StorageService);

  private readonly errorHandlerService = inject(AmlAdminErrorHandlerService);

  private readonly navigationFacade = inject(NavigationFacade);

  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly destroyRef = inject(DestroyRef);

  protected readonly showMaintenanceNotice = this.maintenanceService.getShowMaintenanceNoticePreference();

  private readonly noticeMessageTranslationParams = computed(() => {
    const parameters = this.maintenanceService.maintenanceNoticeDetails();
    return {
      date: parameters?.maintenanceDate,
      startTime: parameters?.startTime,
      endTime: parameters?.endTime,
    };
  });

  protected readonly maintenanceMessage = this.translationService.translateWithParams(
    'errorPages.maintenance.notice',
    this.noticeMessageTranslationParams,
  );

  public ngOnInit(): void {
    this.loadMaintenanceStatus();
    this.setupBreakpointObserver();
  }

  /**
   * Handles the closure of the maintenance warning notice by updating the user's preference in storage.
   * This prevents the notice from being displayed again in future sessions until the user logins again or the preference is reset.
   *
   * @returns {void} This method does not return any value.
   */
  public onMaintenanceWarningClose(): void {
    this.storageService.set(StorageServiceKeysMap.ShowMaintenanceNotice, false);
  }

  /**
   * Sets up a breakpoint observer to monitor changes in screen size based on predefined media queries.
   * Subscribes to these changes and triggers corresponding updates to the navigation state.
   * Uses a destruction reference to unsubscribe automatically.
   *
   * @returns {void} No return value.
   */
  private setupBreakpointObserver(): void {
    this.breakpointObserver
      .observe(NAVIGATION_BREAKPOINTS.map((bp) => bp.mediaQuery))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const matched = NAVIGATION_BREAKPOINTS.find((bp) => result.breakpoints[bp.mediaQuery]);
        if (matched) {
          this.navigationFacade.setScreen(matched.screen);
          this.navigationFacade.close();
        }
      });
  }

  /**
   * Loads the maintenance status from the maintenance service.
   * This method is called during the component initialization to ensure that the maintenance status is fetched
   *
   * @returns {void} This method does not return any value.
   * @private
   */
  private loadMaintenanceStatus(): void {
    this.maintenanceService.loadMaintenanceStatus().catch((error) => {
      this.errorHandlerService.handleError({
        error,
        metadata: {
          severity: ErrorSeverityMap.High,
          component: 'LayoutComponent',
          action: 'Error fetching maintenance info.',
        },
      });
    });
  }
}
